import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SpeedDial,
  SpeedDialIcon,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DesktopDatePicker from "@mui/lab/DesktopDatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import api from "../../../components/services/api";
import DialogAddTruckSchedule from "../components/DialogAddTruckSchedule";

const ALL = "all";

const formatDateTime = ({ value }) => (value ? moment(value).format("DD-MM-YYYY HH:mm") : "-");

// คลิกเลข VOY -> หน้ารายละเอียดของรอบ เปิดแท็บขานั้น
const voyLink = (leg) => ({ row, value }) => (
  <Link to={`/Truck/ViewTruckSchedule/${row.VoyageScheduleTruckId}?leg=${leg}`}>{value}</Link>
);

const columns = [
  { field: "VoyageIn", headerName: "VOY ขาไป", width: 130, renderCell: voyLink("Inbound") },
  { field: "TripsIn", headerName: "เที่ยว", width: 70, type: "number" },
  { field: "RouteIn", headerName: "FROM - TO", width: 200, sortable: false, valueGetter: ({ row }) => `${row.LocFrom} - ${row.LocTo}` },
  { field: "ETDIn", headerName: "ETD", width: 150, valueFormatter: formatDateTime },
  { field: "ETAIn", headerName: "ETA", width: 150, valueFormatter: formatDateTime },
  { field: "VoyageOut", headerName: "VOY ขากลับ", width: 130, renderCell: voyLink("Outbound") },
  { field: "TripsOut", headerName: "เที่ยว", width: 70, type: "number" },
  { field: "RouteOut", headerName: "FROM - TO", width: 200, sortable: false, valueGetter: ({ row }) => `${row.LocTo} - ${row.LocFrom}` },
  { field: "ETDOut", headerName: "ETD", width: 150, valueFormatter: formatDateTime },
  { field: "ETAOut", headerName: "ETA", width: 150, valueFormatter: formatDateTime },
];

function createInitialFilter() {
  return {
    countryId: ALL,
    locationId: ALL,
    voyNo: "",
    dateStart: new Date(),
    dateEnd: moment().add(1, "M").toDate(),
  };
}

// ค่า "ทั้งหมด" / ช่องว่าง ไม่ส่งไป API (axios ตัด undefined ออกเอง)
function toListParams(filter) {
  return {
    startDate: moment(filter.dateStart).format("YYYY-MM-DD"),
    endDate: moment(filter.dateEnd).format("YYYY-MM-DD"),
    voyNo: filter.voyNo.trim() || undefined,
    countryId: filter.countryId === ALL ? undefined : filter.countryId,
    locationId: filter.locationId === ALL ? undefined : filter.locationId,
  };
}

function FilterSelect({ label, value, options, valueKey, labelKey, onChange }) {
  return (
    <FormControl size="small" sx={{ width: "15%", marginRight: "10px" }}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} onChange={(e) => onChange(e.target.value)}>
        <MenuItem value={ALL}>ทั้งหมด</MenuItem>
        {options.map((option) => (
          <MenuItem key={option[valueKey]} value={option[valueKey]}>
            {option[labelKey]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

async function fetchActive(url) {
  try {
    const res = await api.get(url);
    return Array.isArray(res?.data) ? res.data.filter((x) => x?.Status === true) : [];
  } catch (e) {
    console.log(e);
    return [];
  }
}

export default function TruckSchedule() {
  const MenuActive = useSelector((state) => state.MenuActive.List);
  const canEdit = MenuActive?.some((x) => x.ActionNo === "A033");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [countries, setCountries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState(createInitialFilter);
  const [listParams, setListParams] = useState(() => toListParams(filter));

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("api/truck/list", { params: listParams });
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [listParams]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    fetchActive("api/country/list").then(setCountries);
    fetchActive("api/location/list").then(setLocations);
  }, []);

  const setFilterField = (name, value) => setFilter((prev) => ({ ...prev, [name]: value }));

  const handleCountryChange = (countryId) => setFilter((prev) => ({ ...prev, countryId, locationId: ALL }));

  const locationOptions = filter.countryId === ALL ? locations : locations.filter((x) => x.CountryId === filter.countryId);

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "warning", text: "กรุณาเลือกรอบที่ต้องการลบ" });
      return;
    }
    const confirm = await Swal.fire({
      icon: "warning",
      text: `ลบรอบรถที่เลือก ${selectedIds.length} รอบ (ทั้งขาไปและขากลับ) ?`,
      showCancelButton: true,
      confirmButtonText: "ลบ",
      cancelButtonText: "ยกเลิก",
      confirmButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    try {
      await api.post("api/truck/delete", { VoyageScheduleTruckIds: selectedIds });
      setSelectedIds([]);
      loadList();
    } catch (err) {
      Swal.fire({ icon: "error", text: err?.response?.data?.message || "ลบไม่สำเร็จ" });
    }
  };

  return (
    <div>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card variant="outlined" style={{ padding: "20px", margin: "25px" }}>
          <h6 style={{ marginTop: "10px" }} className="textcolorgreen">
            ตารางรถหัวลาก
          </h6>
          <Grid container alignItems="center" spacing={1}>
            <Grid item xs>
              <Typography className="textcolordarkblue" variant="h5" component="h1">
                Truck Schedule
              </Typography>
            </Grid>
            <Grid item sx={{ display: "flex", flexDirection: "row" }}>
              <Typography className="textcolorBule">Truck</Typography>
              <Typography>
                <span>&nbsp;</span> / Truck Schedule
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ marginTop: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              {canEdit && (
                <Button
                  onClick={handleDelete}
                  style={{ backgroundColor: "red", height: "35px", color: "#fafafa" }}
                >
                  <DeleteIcon />
                </Button>
              )}
            </div>
            <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1, justifyContent: "flex-end" }}>
              <FilterSelect
                label="Country"
                value={filter.countryId}
                options={countries}
                valueKey="CountryId"
                labelKey="CountryName"
                onChange={handleCountryChange}
              />
              <FilterSelect
                label="Location"
                value={filter.locationId}
                options={locationOptions}
                valueKey="LocationId"
                labelKey="LocationName"
                onChange={(value) => setFilterField("locationId", value)}
              />
              <TextField
                size="small"
                label="Search Voy"
                value={filter.voyNo}
                onChange={(e) => setFilterField("voyNo", e.target.value)}
              />
              <DesktopDatePicker
                label="Date Start"
                inputFormat="yyyy/MM/dd"
                value={filter.dateStart}
                onChange={(value) => setFilterField("dateStart", value)}
                renderInput={(params) => <TextField size="small" style={{ width: "200px", marginLeft: "10px" }} {...params} />}
              />
              <DesktopDatePicker
                label="Date End"
                inputFormat="yyyy/MM/dd"
                value={filter.dateEnd}
                minDate={filter.dateStart}
                onChange={(value) => setFilterField("dateEnd", value)}
                renderInput={(params) => <TextField size="small" style={{ width: "200px", marginLeft: "10px" }} {...params} />}
              />
              <Button
                style={{ backgroundColor: "#0d6efd", color: "#fafafa", marginLeft: "10px" }}
                onClick={() => setListParams(toListParams(filter))}
              >
                <SearchIcon />
              </Button>
            </Box>
          </Box>

          <Box sx={{ height: 650, width: "100%", marginTop: "30px" }}>
            <DataGrid
              getRowId={(row) => row.VoyageScheduleTruckId}
              rows={rows}
              columns={columns}
              loading={loading}
              pageSize={10}
              rowsPerPageOptions={[10]}
              disableColumnFilter
              disableColumnMenu
              disableSelectionOnClick
              checkboxSelection={canEdit}
              selectionModel={selectedIds}
              onSelectionModelChange={setSelectedIds}
            />
          </Box>
        </Card>
      </LocalizationProvider>
      {canEdit && (
        <SpeedDial
          ariaLabel="Create truck schedule"
          sx={{ position: "absolute", bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
          onClick={() => setOpenAdd(true)}
        />
      )}
      <DialogAddTruckSchedule open={openAdd} onClose={() => setOpenAdd(false)} onCreated={loadList} />
    </div>
  );
}
